// System
import { useSnackbar } from 'notistack';
// Redux
import { useSelector, useDispatch } from 'react-redux';
import {
  setShowUserDrawer,
  setSelectedUser,
  setShowDeleteUserModal,
  setShowCancelUserDrawer,
} from '@Modal/reducers/users/drawer/userDrawerSlice';
// Types
import { AppDispatch } from 'src/app/store';
import { StoreInterface } from 'src/app/types';
import {
  GetUsersDocument,
  useDeleteUserMutation,
  useGetUserLazyQuery,
  useUpdateUserMutation,
} from '@Src/generated.graphql';
import { BaseSyntheticEvent, useCallback, useEffect } from 'react';
import { useManageSetupUserSchema, useSetupUserForm } from '@Forms/users/SetupUserFormContext';
import { useSetupUserUseCaseMapper } from '@Hooks/users/SetupUser/useSetupUserUseCaseMapper';
import { useSetupUserUseCaseSchema } from '@Hooks/users/SetupUser/useSetupUserUseCaseSchema';
import { useFormState } from 'react-hook-form';

export const useUserDrawer = () => {
  const [getUser] = useGetUserLazyQuery();
  const [updateUser] = useUpdateUserMutation();
  const [deleteUser] = useDeleteUserMutation();
  // Redux
  const dispatch = useDispatch<AppDispatch>();
  const { selectedUser, showUserDrawer, showDeleteUserModal, showCancelUserDrawer } = useSelector(
    (state: StoreInterface) => state?.userDrawer
  );

  // Other Hooks
  const { enqueueSnackbar } = useSnackbar();

  const { setSchema } = useManageSetupUserSchema();
  const mapper = useSetupUserUseCaseMapper(selectedUser);
  const schema = useSetupUserUseCaseSchema();
  const { control, handleSubmit, reset } = useSetupUserForm();
  const { isDirty, errors } = useFormState({ control });

  const setUserDrawerMapperHandler = useCallback(async () => {
    setSchema(schema);
    const formState = await mapper.toFormState();
    reset(formState);
  }, [selectedUser]);

  useEffect(() => {
    if (selectedUser) {
      setUserDrawerMapperHandler();
    }
  }, [selectedUser, setUserDrawerMapperHandler]);

  const handleUserRowClick = async (id: string) => {
    console.log('id', id);
    try {
      const { data } = await getUser({ variables: { id: id } });
      if (data && data.user) {
        await dispatch(setSelectedUser(data.user));
      }
      dispatch(setShowUserDrawer(true));
    } catch (error) {
      enqueueSnackbar(error, {
        autoHideDuration: 2000,
        variant: 'error',
        anchorOrigin: {
          vertical: 'bottom',
          horizontal: 'right',
        },
      });
    }
  };

  const handleSubmitGeneralForm = useCallback(
    (e?: BaseSyntheticEvent) => {
      void handleSubmit(async (formData) => {
        const isProviderUsersAdmin =
          typeof formData.isProviderUsersAdmin === 'boolean'
            ? formData.isProviderUsersAdmin
            : formData.isProviderUsersAdmin === 'true';

        const { password, ...formWithoutPassword } = formData;
        const newFormData = {
          ...formWithoutPassword,
          ...(password ? { password } : {}),
          roleIds: Array.isArray(formData?.roleIds) ? formData?.roleIds : [formData?.roleIds],
          userId: selectedUser?.id,
          isProviderUsersAdmin,
        };

        try {
          await updateUser({
            // @ts-ignore
            variables: { input: newFormData },
            refetchQueries: [{ query: GetUsersDocument }],
            awaitRefetchQueries: true,
          });
          enqueueSnackbar('User was successfully updated', {
            variant: 'success',
            autoHideDuration: 2000,
            anchorOrigin: {
              vertical: 'bottom',
              horizontal: 'right',
            },
          });
          dispatch(setShowUserDrawer(false));
        } catch (error) {
          enqueueSnackbar(error.message, {
            variant: 'error',
            autoHideDuration: 2000,
            anchorOrigin: {
              vertical: 'bottom',
              horizontal: 'right',
            },
          });
        }
      })(e);
    },
    [dispatch, enqueueSnackbar, handleSubmit, selectedUser?.id, updateUser]
  );

  const handleDeleteUser = async () => {
    try {
      await deleteUser({
        variables: { userId: selectedUser?.id },
        refetchQueries: [{ query: GetUsersDocument }],
        awaitRefetchQueries: true,
      });
      enqueueSnackbar('User was successfully Deleted', {
        variant: 'success',
        autoHideDuration: 2000,
        anchorOrigin: {
          vertical: 'bottom',
          horizontal: 'right',
        },
      });
      dispatch(setShowUserDrawer(false));
      dispatch(setShowDeleteUserModal(false));
    } catch (error) {
      enqueueSnackbar(error.message, {
        variant: 'error',
        autoHideDuration: 2000,
        anchorOrigin: {
          vertical: 'bottom',
          horizontal: 'right',
        },
      });
    }
  };

  const handleCancelTakeMeBack = (action: string) => {
    if (action === 'back') {
      dispatch(setShowCancelUserDrawer(false));
      dispatch(setShowUserDrawer(true));
    } else {
      reset();
      dispatch(setShowCancelUserDrawer(false));
      dispatch(setShowUserDrawer(false));
    }
  };

  const handleCancelChanges = () => {
    dispatch(setShowCancelUserDrawer(false));
    dispatch(setShowUserDrawer(false));
    reset();
  };

  const handleCreateCancel = () => {
    if (isDirty) {
      return dispatch(setShowCancelUserDrawer(true));
    } else {
      return dispatch(setShowUserDrawer(false));
    }
  };

  return {
    // Store
    selectedUser,
    showUserDrawer,
    showDeleteUserModal,
    showCancelUserDrawer,
    submittable: isDirty && Object.values(errors).length == 0,
    // Actions
    setSelectedUser,
    setShowUserDrawer,
    setShowDeleteUserModal,
    setShowCancelUserDrawer,
    // Handlers
    handleDeleteUser,
    handleCreateCancel,
    handleUserRowClick,
    handleCancelChanges,
    handleCancelTakeMeBack,
    handleSubmitGeneralForm,
  };
};
